import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';

import { categoriesArrayPropTypes, userPropTypes } from 'helpers/propTypes';
import LogoContainer from 'containers/LogoContainer';
import Menu from 'components/main/Header/Menu';
import SearchIcon from '@material-ui/icons/Search';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { TextField } from '@material-ui/core';

import './NavbarNeo.scss';

const NavbarNeo = ({
  cartSize, categories, closeMenu, isMenuActive, isUserLoggedIn,
  openMenu, openSearchModal, user, show, client, subCategories,
  products = [],
  setSelectedProduct,
  selectedProduct,
  onSearchProductChange,
  searchProduct = '',
  onSubmit,
  hasFrequentQuestions = false,
}) => {
  // Bloquear el scroll del body cuando el menú móvil está abierto
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = isMenuActive ? 'hidden' : prev || '';
    return () => { document.body.style.overflow = prev || ''; };
  }, [isMenuActive]);

  const renderSearchOption = (opt) => {
    if (typeof opt === 'string') {
      return (
        <div className="search-item">
          <div className="search-item__img" />
          <div className="search-item__info">
            <div className="search-item__title">{opt}</div>
          </div>
        </div>
      );
    }
    const price = opt?.price ?? 0;
    return (
      <div className="search-item" data-testid={`search-item-${opt?.id || 's'}`}>
        <div className="search-item__img">
          <img src={opt?.image1 || '/images/placeholder.png'} alt={opt?.name || ''} loading="lazy" />
        </div>
        <div className="search-item__info">
          <div className="search-item__title" title={opt?.name || ''}>{opt?.name || ''}</div>
          <div className="search-item__price">
            {opt?.sale ? (
              <>
                <span className="search-item__price--sale">${opt.sale?.toLocaleString('de-DE')}</span>
                <span className="search-item__price--old">${price?.toLocaleString('de-DE')}</span>
              </>
            ) : (<span>${price.toLocaleString('de-DE')}</span>)}
          </div>
        </div>
      </div>
    );
  };

  const handleSearchSubmit = () => {
    if (typeof onSubmit === 'function') onSubmit();
  };

  return (
    <div className="navbar-neo" data-testid="navbar">
      <div className="neo-container">
        <nav className="neo-row">
          {/* Hamburguesa */}
          <button
            className="hamburger-neo"
            onClick={isMenuActive ? closeMenu : openMenu}
            type="button"
            aria-label={isMenuActive ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={isMenuActive}
            aria-controls="neo-drawer"
          >
            <i className={`fa ${isMenuActive ? 'fa-times' : 'fa-bars'}`} />
          </button>

          {/* Logo */}
          <LogoContainer />

          {/* Menú horizontal (desktop) */}
          <Menu
            categories={categories}
            subCategories={subCategories}
            closeMenu={closeMenu}
            isUserLoggedIn={isUserLoggedIn}
            ulClassName="navbar-menu-neo"
            client={client}
            hasFrequentQuestions={hasFrequentQuestions}
          />

          {/* Acciones */}
          <div className="neo-actions">
            <div className="man-searchShell">
                <div className="man-searchBar">
                  <SearchIcon className="man-searchIcon" />
                  <div className="man-searchInput">
                    <Autocomplete
                      key="autocomplete-desktop"
                      options={products}
                      freeSolo
                      filterOptions={(opts) => opts}
                      onChange={(e, product) => setSelectedProduct(e, product)}
                      value={selectedProduct ?? null}
                      onInputChange={onSearchProductChange}
                      inputValue={searchProduct}
                      getOptionSelected={(o, p) => o?.id === p?.id}
                      getOptionLabel={(o) => (typeof o === 'string' ? o : (o?.name ?? ''))}
                      renderOption={renderSearchOption}
                      noOptionsText="Sin resultados"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Buscar..."
                          onKeyDown={(e) => { if (e.key === 'Enter') handleSearchSubmit(); }}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>

            {show && (
              <Link to="/carrito" className="neo-cart" aria-label="Carrito">
                <i className="fa fa-shopping-cart" aria-hidden="true" />
                <span className="neo-cart-count">{cartSize}</span>
              </Link>
            )}
          </div>
        </nav>
      </div>

      {/* Overlay (mobile) */}
      {isMenuActive && <div className="neo-overlay" onClick={closeMenu} />}

      {/* Drawer (mobile) */}
      <aside
        id="neo-drawer"
        className={`neo-drawer ${isMenuActive ? 'is-open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Menú principal"
      >
        <div className="neo-drawer-header">
          <LogoContainer />
          <button className="neo-drawer-close" onClick={closeMenu} type="button" aria-label="Cerrar">
            <i className="fa fa-times" />
          </button>
        </div>

        <div className="neo-drawer-search">
          <button
            type="button"
            className="neo-search neo-search--full"
            onClick={() => { closeMenu(); openSearchModal(); }}
          >
            <i className="fa fa-search" aria-hidden="true" />
            <span>Buscar...</span>
          </button>
        </div>

        {/* Menú vertical (mobile) */}
        <Menu
          categories={categories}
          subCategories={subCategories}
          closeMenu={closeMenu}
          isUserLoggedIn={isUserLoggedIn}
          ulClassName="neo-mobile-nav"
          client={client}
          isMobile
          hasFrequentQuestions={hasFrequentQuestions}
        />

        {show && (
          <Link to="/carrito" className="neo-mobile-cart" onClick={closeMenu}>
            <i className="fa fa-shopping-cart" />
            <span>Ver carrito ({cartSize})</span>
          </Link>
        )}
      </aside>
    </div>
  );
};

NavbarNeo.defaultProps = { user: null };
NavbarNeo.propTypes = {
  cartSize: PropTypes.number.isRequired,
  categories: categoriesArrayPropTypes.isRequired,
  closeMenu: PropTypes.func.isRequired,
  isMenuActive: PropTypes.bool.isRequired,
  isUserLoggedIn: PropTypes.bool.isRequired,
  openMenu: PropTypes.func.isRequired,
  openSearchModal: PropTypes.func.isRequired,
  user: userPropTypes,
  show: PropTypes.bool,
  client: PropTypes.shape().isRequired,
  subCategories: PropTypes.arrayOf(PropTypes.shape()).isRequired,
};

export default NavbarNeo;
